import React from 'react';
import { FormComponent, FormComponentProps } from '../index';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import FormField from '../../common/form-field';
import { parseError } from '../../../services/utils';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import css from './waypoint.module.css';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';

export default function Waypoint(
  name: string,
  label: string = null
): FormComponent {
  return ({ ...rest }) => (
    <WaypointComponent name={name} label={label} {...rest} />
  );
}

export function WaypointComponent({
  name,
  label,
  parentName,
  disabled,
  required,
}: FormComponentProps & {
  name: string;
  label?: string;
  required?: string[];
}) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();
  const waypoint = parentName ? `${parentName}.${name}` : name;
  const error = parseError(waypoint, errors);
  const field = watch(name);

  if (!field?.length) {
    return <></>;
  }

  const onSuffixDelete = (index) => {
    let unmutate = [...field];
    setValue('deleteWaypoint', unmutate[index].id);

    unmutate.splice(index, 1);
    setValue(waypoint, unmutate);
  };

  return (
    <FormField
      label={label ? t(label) : t(name)}
      labelFor={waypoint}
      required={required?.includes(waypoint)}
      error={error ? error.message : null}
    >
      {field?.map((_, index) => (
        <div key={index} className={index && css['more-than-one']}>
          <WaypointInputComponent
            name={waypoint + '.' + index + '.label'}
            disabled={disabled}
            index={index}
            suffixOnClick={onSuffixDelete}
            {...register(waypoint + '.' + index + '.label')}
          />
        </div>
      ))}
    </FormField>
  );
}

interface TextType {
  name: string;
  readonly?: boolean;
  disabled?: boolean;
  prefix?: IconDefinition;
  suffix?: IconDefinition;
  index: number;
  suffixOnClick: (e: number) => void;
}

export function WaypointInputComponent({
  name,
  readonly,
  disabled,
  prefix,
  suffix,
  index,
  suffixOnClick,
}: TextType) {
  const { register } = useFormContext();
  const prefixIconElement = prefix ? (
    <FontAwesomeIcon icon={prefix} className={css['input-icon-prefix']} />
  ) : (
    <FontAwesomeIcon
      icon={faMapMarkerAlt}
      className={css['input-icon-prefix']}
    />
  );

  const suffixIconElement = suffix ? (
    <FontAwesomeIcon
      icon={suffix}
      className={css['input-icon-suffix']}
      onClick={() => suffixOnClick(index)}
    />
  ) : (
    <FontAwesomeIcon
      icon={faTimes}
      className={css['input-icon-suffix']}
      size={'lg'}
      onClick={() => suffixOnClick(index)}
    />
  );

  return (
    <div className={css['input-container']}>
      {prefixIconElement}
      <input
        type={'text'}
        name={name}
        className={css['input']}
        {...register(name)}
        readOnly={readonly ?? false}
        disabled={disabled ?? false}
      />
      {suffixIconElement}
    </div>
  );
}
